import { useState } from 'react';
import { Plus, X, Upload } from 'lucide-react';
import { ExcelImportModal } from '../../../components/prioritization/import-export';
import type { AppField } from '../../../components/prioritization/import-export/ColumnMapper';

// Field definitions for Excel import
const importFields: AppField[] = [
  { key: 'name', label: 'Name', required: true, type: 'string' },
  { key: 'description', label: 'Description', required: false, type: 'string' },
];

interface ItemInputProps {
  items: string[];
  onItemsChange: (items: string[]) => void;
  onStart: () => void;
}

export function ItemInput({ items, onItemsChange, onStart }: ItemInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      onItemsChange([...items, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleExcelImport = (data: Record<string, unknown>[]) => {
    // Extract names from imported data
    const importedNames = data
      .map(row => String(row.name || '').trim())
      .filter(name => name.length > 0);
    
    // Combine with existing items
    const combined = [...items, ...importedNames];
    
    // Remove duplicates while preserving order
    const unique = combined.filter(
      (name, index, self) => 
        index === self.findIndex(n => n.toLowerCase() === name.toLowerCase())
    );
    
    onItemsChange(unique);
    setShowImportModal(false);
  };

  const canStart = items.length >= 2;

  return (
    <>
      {/* Excel Import Modal */}
      <ExcelImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleExcelImport}
        appFields={importFields}
        title="Import Items from Excel"
        description="Import a list of items to rank from an Excel file. Each row should have a name column."
      />

      <div className="max-w-2xl mx-auto">
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">
            Add Items to Rank
          </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Enter the items you want to compare and rank. You'll compare them two at a time.
        </p>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter an item name..."
            className="flex-1 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleAdd}
            disabled={!inputValue.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add
          </button>
        </div>
        
        <button
          onClick={() => setShowImportModal(true)}
          className="mb-6 flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          <Upload className="h-4 w-4" />
          Import from Excel
        </button>

        {items.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">
              Items ({items.length})
            </h3>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-[var(--surface)] border border-[var(--rule)]"
                >
                  <span className="text-[var(--text-pri)]">{item}</span>
                  <button
                    onClick={() => handleRemove(index)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={onStart}
          disabled={!canStart}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-medium text-lg transition-colors"
        >
          {canStart
            ? `Start Ranking (${items.length} items)`
            : 'Add at least 2 items to start'}
        </button>

        {items.length >= 2 && (
          <p className="mt-4 text-sm text-[var(--text-ter)] text-center">
            You'll make {Math.floor((items.length * (items.length - 1)) / 2)} comparisons
          </p>
        )}
        </div>
      </div>
    </>
  );
}

